import { Component, Input, Output, EventEmitter, computed, forwardRef, signal } from '@angular/core';

import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { ToggleVariant, ToggleSize } from '../models/toggle.model';

const toggleVariants = cva({
  base: [
    'relative inline-flex items-center rounded-full border-2 transition-all duration-300 ease-out',
    'cursor-pointer backdrop-blur-md transform-gpu',
    'hover:scale-105 active:scale-95',
    'focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-2 focus:outline-none',
    'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-60 before:rounded-full before:pointer-events-none',
    'before:transition-opacity before:duration-300',
    // Minimum 44px touch target (sm track is 16px tall; 16 + 14*2 = 44)
    'after:content-[""] after:absolute after:-inset-y-3.5 after:-inset-x-1 after:rounded-xl',
  ].join(' '),
  variants: {
    variant: {
      default: [
        // Off state
        'border-white/30 bg-white/10',
        'hover:border-white/50 hover:bg-white/15',
        'dark:border-white/20 dark:bg-white/5',
        'dark:hover:border-white/35 dark:hover:bg-white/10',
      ].join(' '),
      elevated: [
        // Off state
        'border-gray-300/40 bg-white/15 shadow-lg shadow-gray-500/10',
        'hover:border-gray-300/60 hover:bg-white/20 hover:shadow-xl hover:shadow-gray-500/15',
        'dark:border-gray-600/30 dark:bg-white/8 dark:shadow-lg dark:shadow-black/15',
        'dark:hover:border-gray-600/50 dark:hover:bg-white/12',
      ].join(' '),
      premium: [
        // Ultra-premium for e-commerce
        'border-gray-300/50 bg-gradient-to-r from-white/20 via-white/15 to-gray-50/20',
        'shadow-xl shadow-gray-500/20',
        'hover:border-gray-300/70 hover:from-white/25 hover:via-white/20 hover:to-gray-50/30',
        'hover:shadow-2xl hover:shadow-gray-500/30',
        'dark:border-gray-600/40 dark:from-white/15 dark:via-white/10 dark:to-gray-900/20',
        'dark:shadow-xl dark:shadow-black/25',
        'dark:hover:border-gray-600/60 dark:hover:from-white/20',
      ].join(' '),
      minimal: [
        'border-gray-200/30 bg-white/8',
        'hover:border-gray-200/50 hover:bg-white/12',
        'dark:border-gray-600/20 dark:bg-white/3',
        'dark:hover:border-gray-600/35 dark:hover:bg-white/6',
      ].join(' '),
    },
    size: {
      sm: 'w-8 h-4',
      md: 'w-10 h-5',
      lg: 'w-12 h-6',
      xl: 'w-16 h-8',
    },
    checked: {
      true: [
        // On state styling
        'border-primary-500/70 bg-primary-500/20',
        'shadow-lg shadow-primary-500/30',
        'dark:border-primary-400/80 dark:bg-primary-400/25',
        'dark:shadow-lg dark:shadow-primary-500/40',
      ].join(' '),
      false: '',
    },
    state: {
      default: '',
      error: 'border-red-400/50 focus:border-red-500/60 focus:ring-red-500/50',
      success: 'border-cyan-400/50 focus:border-cyan-500/60 focus:ring-cyan-500/50',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    checked: 'false',
    state: 'default',
  },
});

const thumbVariants = cva({
  base: [
    'absolute rounded-full shadow-lg transition-all duration-300 ease-out',
    'backdrop-blur-sm border border-white/50',
    // Removed transform-gpu as it conflicts with inline style binding
    'group-hover:scale-110',
    'pointer-events-none', // Allow clicks to pass through to label
    // Glass morphism thumb
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-80 before:rounded-full before:pointer-events-none',
  ].join(' '),
  variants: {
    variant: {
      default: ['bg-white/90 border-white/60 shadow-gray-500/20', 'dark:bg-white/80 dark:border-white/40'].join(' '),
      elevated: [
        'bg-white/95 border-white/70 shadow-lg shadow-gray-500/30',
        'dark:bg-white/85 dark:border-white/50',
      ].join(' '),
      premium: [
        'bg-gradient-to-br from-white/95 via-white/90 to-primary-50/80',
        'border-white/80 shadow-xl shadow-gray-500/40',
        'dark:bg-gradient-to-br dark:from-white/90 dark:via-white/80 dark:to-primary-900/60',
        'dark:border-white/60',
      ].join(' '),
      minimal: ['bg-white/85 border-white/50 shadow-gray-500/15', 'dark:bg-white/75 dark:border-white/35'].join(' '),
    },
    size: {
      sm: 'w-3 h-3',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
      xl: 'w-7 h-7',
    },
    checked: {
      true: [
        'bg-primary-600/90 border-primary-400/70',
        'shadow-primary-500/30',
        'dark:bg-primary-400/90 dark:border-primary-300/70',
      ].join(' '),
      false: '',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
    checked: 'false',
  },
});

const labelVariants = cva({
  base: [
    'cursor-pointer transition-all duration-200 ease-out',
    'hover:text-gray-900 dark:hover:text-white',
    'select-none',
  ].join(' '),
  variants: {
    size: {
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
    },
    disabled: {
      true: 'opacity-50 cursor-not-allowed',
      false: 'text-gray-700 dark:text-gray-200',
    },
  },
  defaultVariants: {
    size: 'md',
    disabled: 'false',
  },
});

@Component({
  selector: 'ui-toggle-glass',
  standalone: true,
  imports: [IconGlassComponent],
  host: {
    '[class.toggle-glass--navy-button]': 'appearance === "navy-button"',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ToggleGlassComponent),
      multi: true,
    },
  ],
  templateUrl: './toggle-glass.component.html',
  styles: [`
    /* Use CSS custom property with translate for smooth GPU-accelerated animation */
    :host label[role="switch"] > div {
      translate: var(--thumb-x, 2px) 0;
      transition: translate 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      will-change: translate;
    }

    /* Ensure label has proper positioning context */
    :host label[role="switch"] {
      position: relative !important;
    }

    :host.toggle-glass--navy-button label[role="switch"] {
      border-width: 1px !important;
      border-color: rgba(148, 163, 184, 0.22) !important;
      border-radius: 9999px !important;
      overflow: hidden !important;
      background-color: transparent !important;
      background-image: linear-gradient(180deg, rgba(28, 46, 74, 0.72) 0%, rgba(16, 29, 49, 0.84) 100%) !important;
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.04),
        0 4px 6px -1px rgba(2, 8, 23, 0.2),
        0 2px 4px -2px rgba(2, 8, 23, 0.2) !important;
    }

    :host.toggle-glass--navy-button label[role="switch"]:hover {
      border-color: rgba(166, 180, 201, 0.3) !important;
      background-image: linear-gradient(180deg, rgba(31, 52, 84, 0.7) 0%, rgba(22, 38, 63, 0.86) 100%) !important;
      box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.06),
        0 4px 6px -1px rgba(2, 8, 23, 0.28),
        0 2px 4px -2px rgba(2, 8, 23, 0.28) !important;
    }
  `],
})
export class ToggleGlassComponent implements ControlValueAccessor {
  @Input() label: string = '';
  @Input() description: string = '';
  @Input() variant: ToggleVariant = 'default';
  @Input() size: ToggleSize = 'md';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() showIcons: boolean = false;
  @Input() showLabels: boolean = false;
  @Input() onIcon: string = 'check';
  @Input() offIcon: string = 'x';
  @Input() onLabel: string = 'ON';
  @Input() offLabel: string = 'OFF';
  @Input() appearance: 'default' | 'navy-button' = 'default';
  @Input() errorMessage: string = '';
  @Input() successMessage: string = '';
  @Input() themeSection: ThemeSection = 'global';

  @Input() set checked(value: boolean) {
    this._checked.set(!!value);
  }
  get checked(): boolean {
    return this._checked();
  }

  @Output() checkedChange = new EventEmitter<boolean>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  protected toggleId = `toggle-${Math.random().toString(36).substring(2, 11)}`;
  protected _checked = signal(false);

  // ControlValueAccessor
  private onChange = (_value: boolean) => {};
  private onTouched = () => {};

  public thumbIconSize = computed(() => {
    const sizeMap = {
      sm: 'xs' as const,
      md: 'xs' as const,
      lg: 'sm' as const,
      xl: 'sm' as const,
    };
    return sizeMap[this.size];
  });

  public currentIcon = computed(() => {
    return this._checked() ? this.onIcon : this.offIcon;
  });

  public thumbTranslate = computed(() => {
    const offsetMap = {
      sm: this._checked() ? '16px' : '2px',
      md: this._checked() ? '20px' : '2px',
      lg: this._checked() ? '24px' : '2px',
      xl: this._checked() ? '32px' : '2px',
    };
    return offsetMap[this.size];
  });

  public computedToggleClasses = computed(() => {
    const state = this.errorMessage ? 'error' : this.successMessage ? 'success' : 'default';
    return toggleVariants({
      variant: this.variant,
      size: this.size,
      checked: this._checked() ? 'true' : 'false',
      state,
    });
  });

  public computedThumbClasses = computed(() => {
    return thumbVariants({
      variant: this.variant,
      size: this.size,
      checked: this._checked() ? 'true' : 'false',
    });
  });

  public computedLabelClasses = computed(() => {
    return labelVariants({
      size: this.size,
      disabled: this.disabled ? 'true' : 'false',
    });
  });

  public handleChange(event: Event): void {
    if (this.disabled) return;

    const target = event.target as HTMLInputElement;
    const newValue = target.checked;

    this._checked.set(newValue);
    this.onChange(newValue);
    this.checkedChange.emit(newValue);
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.toggle();
    }
  }

  public onFocus(): void {
    this.focus.emit();
  }

  public onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  toggle(): void {
    if (this.disabled) return;

    const newValue = !this._checked();
    this._checked.set(newValue);
    this.onChange(newValue);
    this.checkedChange.emit(newValue);
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this._checked.set(!!value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}

/**
 * Usage Examples:
 *
 * Basic toggle:
 * <ui-toggle-glass label="Enable notifications"></ui-toggle-glass>
 *
 * Premium e-commerce toggle with icons:
 * <ui-toggle-glass
 *   label="Premium shipping"
 *   description="Get your order in 1-2 business days"
 *   variant="premium"
 *   size="lg"
 *   [showIcons]="true"
 *   onIcon="zap"
 *   offIcon="truck"
 * ></ui-toggle-glass>
 *
 * Toggle with state labels:
 * <ui-toggle-glass
 *   label="Dark mode"
 *   variant="elevated"
 *   [showLabels]="true"
 *   onLabel="DARK"
 *   offLabel="LIGHT"
 * ></ui-toggle-glass>
 *
 * Form integration:
 * <ui-toggle-glass
 *   label="Subscribe to newsletter"
 *   [formControl]="newsletterControl"
 *   [errorMessage]="getErrorMessage('newsletter')"
 *   [required]="true"
 * ></ui-toggle-glass>
 *
 * E-commerce product options:
 * <ui-toggle-glass
 *   label="Gift wrapping"
 *   description="Add beautiful gift wrapping (+$5.99)"
 *   variant="premium"
 *   size="lg"
 *   [(checked)]="giftWrapping"
 *   [showIcons]="true"
 *   onIcon="gift"
 *   offIcon="package"
 * ></ui-toggle-glass>
 *
 * Settings toggles:
 * <ui-toggle-glass
 *   label="Auto-save cart"
 *   description="Automatically save items for later"
 *   variant="minimal"
 *   [(checked)]="autoSave"
 * ></ui-toggle-glass>
 *
 * Different variants:
 * <ui-toggle-glass variant="elevated" label="Premium feature"></ui-toggle-glass>
 * <ui-toggle-glass variant="minimal" label="Simple option"></ui-toggle-glass>
 * <ui-toggle-glass variant="premium" label="Ultra-modern toggle"></ui-toggle-glass>
 */
